import { assertNever } from "@redotech/util/type";
import { ArrayStringFilterBuilder } from "../advanced-filters/array-string-filter";
import { BooleanFilterBuilder } from "../advanced-filters/boolean-filter";
import { DateFilterBuilder } from "../advanced-filters/date-filter";
import { ExistsFilterBuilder } from "../advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  decodeFilterGroup,
  GenericFilterBuilder,
  isFilterGroup,
} from "../advanced-filters/generic-advanced-filter-data";
import { NumberFilterBuilder } from "../advanced-filters/number-filter";
import { StringFilterBuilder } from "../advanced-filters/string-filter";
import { AdvancedFilterData, FilterGroup } from "../views";

export function getFilterBuilder(
  filter: AdvancedFilterData | undefined,
): GenericFilterBuilder<any, any, any> {
  if (!filter) {
    throw new Error("filter is undefined");
  }

  switch (filter.type) {
    case AdvancedFilterType.DATE:
      return DateFilterBuilder;
    case AdvancedFilterType.ARRAY_TO_STRING:
      return ArrayStringFilterBuilder;
    case AdvancedFilterType.NUMBER:
      return NumberFilterBuilder;
    case AdvancedFilterType.STRING:
      return StringFilterBuilder;
    case AdvancedFilterType.EXISTS:
      return ExistsFilterBuilder;
    case AdvancedFilterType.BOOLEAN:
      return BooleanFilterBuilder;
    default:
      assertNever(filter);
  }
}

export function filtersAreEqual(
  filter1: AdvancedFilterData,
  filter2: AdvancedFilterData,
): boolean {
  if (filter1.type !== filter2.type) {
    return false;
  }
  if (filter1.name !== filter2.name) {
    return false;
  }

  const filterBuilder = getFilterBuilder(filter1);
  return (
    filterBuilder.writeToString(filter1 as any) ===
    filterBuilder.writeToString(filter2 as any)
  );
}

export function advancedFiltersAreEqual(
  filters1: AdvancedFilterData[] | undefined,
  filters2: AdvancedFilterData[] | undefined,
): boolean {
  if (!filters1 && !filters2) {
    return true;
  }
  if (!filters1 || !filters2) {
    return false;
  }
  if (filters1.length !== filters2.length) {
    return false;
  }

  for (const [i, element] of filters1.entries()) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (!filtersAreEqual(element, filters2[i])) {
      return false;
    }
  }

  return true;
}

export function isFilterPartial(filter: AdvancedFilterData): boolean {
  const filterBuilder = getFilterBuilder(filter);
  return filterBuilder.isPartial(filter as any);
}

export function isFilterGroupValid(group: FilterGroup | undefined): boolean {
  if (!group) {
    return true;
  }

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      if (!isFilterGroupValid(clause)) {
        return false;
      }
    } else if (isFilterPartial(clause)) {
      return false;
    }
  }

  return true;
}

export function filterGroupsAreEqual(
  group1: FilterGroup | undefined,
  group2: FilterGroup | undefined,
): boolean {
  if (!group1 && !group2) {
    return true;
  }
  if (!group1 || !group2) {
    return false;
  }
  if (group1.operator !== group2.operator) {
    return false;
  }
  if (group1.clauses.length !== group2.clauses.length) {
    return false;
  }

  for (let i = 0; i < group1.clauses.length; i++) {
    const clause1 = group1.clauses[i];
    const clause2 = group2.clauses[i];

    const isGroup1 = isFilterGroup(clause1);
    const isGroup2 = isFilterGroup(clause2);

    if (isGroup1 !== isGroup2) {
      return false;
    }

    if (isGroup1 && isGroup2) {
      if (!filterGroupsAreEqual(clause1, clause2)) {
        return false;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    } else if (!isGroup1 && !isGroup2 && !filtersAreEqual(clause1, clause2)) {
      return false;
    }
  }

  return true;
}

/**
 * Creates a leaf filter decoder function for use with decodeFilterGroup.
 * This consolidates the common logic for parsing "name@@dataString" encoded filters.
 *
 * @param getFilterDataByName - Function that returns the default filter data for a given filter name
 * @returns A decoder function that can be passed to decodeFilterGroup
 */
export function createLeafFilterDecoder(
  getFilterDataByName: (name: string) => AdvancedFilterData | undefined,
): (filterStr: string) => AdvancedFilterData | null {
  return (filterStr: string): AdvancedFilterData | null => {
    const [name, dataString] = filterStr.split("@@");
    if (!name || dataString === undefined) {
      return null;
    }

    const defaultFilter = getFilterDataByName(name);
    if (!defaultFilter) {
      return null;
    }

    const filterBuilder = getFilterBuilder(defaultFilter);
    const data = filterBuilder.readFromString(name, dataString, defaultFilter);
    return data ?? null;
  };
}

/**
 * Decodes a URL-encoded filter group string into a FilterGroup object.
 *
 * @param encoded - The encoded filter group string
 * @param getFilterDataByName - Function that returns the default filter data for a given filter name
 * @returns The decoded FilterGroup or null if parsing fails
 */
export function decodeAdvancedFilterGroup(
  encoded: string | undefined,
  getFilterDataByName: (name: string) => AdvancedFilterData | undefined,
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(
    encoded,
    createLeafFilterDecoder(getFilterDataByName),
  );
}
